import { message } from 'antd';
import type { NavigateFunction } from 'react-router-dom';

import { DEFAULT_UPGRADE_PLAN_SLUG } from '../constants/subscriptionCheckout';
import { createAuthBridgeCode } from '../services/auth';
import type { ICurrentUserMe } from '../services/users/types';
import { clearBridgeReturnUrl } from './bridgeReturnUrlStorage';
import { getDefaultHomePath, isAdmin, isConsultant } from './isProfile';
import type { PlanSignupIntent } from './planCheckout';
import { buildAssinarPath } from './planCheckout';
import type { PostAuthSubscriptionContext } from './resolvePostAuthPath';
import { requiresSubscriptionPayment, resolvePostAuthPath } from './resolvePostAuthPath';

export interface PostAuthRedirectCtx {
  user: ICurrentUserMe;
  navigate: NavigateFunction;
  subscription?: PostAuthSubscriptionContext | null;
  returnUrl: string | null;
  intent?: PlanSignupIntent | null;
}

const resolveAssinarPlanSlug = (
  subscription?: PostAuthSubscriptionContext | null,
  intent?: PlanSignupIntent | null
): string => subscription?.planSlug ?? intent?.planSlug ?? DEFAULT_UPGRADE_PLAN_SLUG;

/**
 * Orquestra o redirecionamento pós-auth:
 * admin/consultant → billing blocked → bridge → plan intent → home.
 */
export async function handlePostAuthRedirect(ctx: PostAuthRedirectCtx): Promise<void> {
  const { user, navigate, subscription, returnUrl, intent } = ctx;

  // Billing bloqueado: não devolve ao WP (só clientes).
  if (!isAdmin(user) && !isConsultant(user) && requiresSubscriptionPayment(subscription)) {
    clearBridgeReturnUrl();
    navigate(buildAssinarPath(resolveAssinarPlanSlug(subscription, intent), 'subscribe'), {
      replace: true,
    });
    return;
  }

  // return_url válido tem prioridade (inclui admin/consultor testando o handshake).
  if (returnUrl) {
    try {
      const { code } = await createAuthBridgeCode({ returnUrl });
      const targetUrl = new URL(returnUrl);
      targetUrl.searchParams.set('code', code);
      clearBridgeReturnUrl();
      window.location.replace(targetUrl.href);
    } catch (error) {
      console.error('Erro no redirecionamento bridge SSO:', error);
      message.error('Não foi possível liberar o acesso ao relatório. Tente novamente.');
      clearBridgeReturnUrl();
      navigate(getDefaultHomePath(user), { replace: true });
    }
    return;
  }

  clearBridgeReturnUrl();

  if (isAdmin(user) || isConsultant(user)) {
    navigate(getDefaultHomePath(user), { replace: true });
    return;
  }

  if (intent) {
    navigate(
      resolvePostAuthPath({
        isAdmin: false,
        isConsultant: false,
        intent,
        subscription,
      }),
      { replace: true }
    );
    return;
  }

  navigate(getDefaultHomePath(user), { replace: true });
}
